import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { useAuth } from "..";

const SecurityContext = createContext(null);

/**
 * THE PROVIDER
 * This must be exported as 'SecurityProvider' for App.jsx to find it.
 */
export function SEC_03_03_SecurityProvider({ children }) {
  const { user, isInitialising: authLoading } = useAuth();
  
  // Hydrate matrix from sessionStorage
  const [matrix, setMatrix] = useState(() => {
    const raw = sessionStorage.getItem('SEC_USER_MATRIX');
    try {
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    const raw = sessionStorage.getItem('SEC_USER_MATRIX');
    if (raw) {
      try {
        setMatrix(JSON.parse(raw));
      } catch (e) {
        console.error("Security Matrix Sync Error", e);
      }
    }
  }, [user]);

  const value = useMemo(() => {
    // Extract role codes (e.g., SITEADMIN)
    const derivedRoles = [...new Set(matrix.map(m => 
      String(m.roleCode || m.RoleCode || "").toUpperCase()
    ))].filter(Boolean);

    const isGlobalAdmin = derivedRoles.includes("GLOBALADMIN") || derivedRoles.includes("SITEADMIN");

    return {
      user,
      isInitialising: authLoading,
      roles: derivedRoles,
      matrix,
      isGlobalAdmin,
      // 🎯 resourceId is the real per-company lock (Auth.SEC_06_00_UserMatrix.ResourceId,
      // joined to Corp.Z_01_CorporateEntity.CorporateEntityId on the backend).
      // divisionCode is a broader classification (currently always 'CORPORATE' for
      // company-scoped rows) — kept as an optional extra filter, not the company key.
      can: (action, division = null, resourceId = null) => {
        if (isGlobalAdmin) return true;
        const act = String(action).toUpperCase();
        const div = division ? String(division).toUpperCase() : null;
        const res = resourceId !== null && resourceId !== undefined ? String(resourceId) : null;
        return matrix.some(m => {
          const mCap = String(m.capabilityCode || "").toUpperCase();
          const mDiv = String(m.divisionCode || "").toUpperCase();
          const mRes = String(m.resourceId ?? m.ResourceId ?? "");
          if (mCap !== act) return false;
          if (div && mDiv !== div) return false;
          if (res !== null && mRes !== res) return false;
          return true;
        });
      },
      // 🎯 Explicit profile check — true only when an active matrix row carries this
      // capability (e.g. KBADMIN). Unlike can(), admins do NOT pass automatically.
      hasCapability: (code) => {
        const want = String(code || "").toUpperCase();
        return matrix.some(m => String(m.capabilityCode || m.CapabilityCode || "").toUpperCase() === want);
      },
      // 🎯 Convenience: every CorporateEntityId (as string) this user holds any
      // active matrix row for — drives filtering "my companies" lists.
      myCorporateEntityIds: [...new Set(
        matrix
          .map(m => m.resourceId ?? m.ResourceId)
          .filter(r => r !== null && r !== undefined && String(r) !== "0")
          .map(r => String(r))
      )]
    };
  }, [user, authLoading, matrix]);

  return (
    <SecurityContext.Provider value={value}>
      {children}
    </SecurityContext.Provider>
  );
}

// 🎯 FIX 1: Explicitly export the Provider name searched by App.jsx
export const SecurityProvider = SEC_03_03_SecurityProvider;

// 🎯 FIX 2: Explicitly export the hook searched by the Guard and Dashboard
export function useSecurity() {
  const context = useContext(SecurityContext);
  if (!context) {
    throw new Error("useSecurity must be used within a SecurityProvider");
  }
  return context;
}